/**
 * SIDEBAR COMPONENT
 * Navigation and feature selection
 */

import React from 'react';
import { MessageCircle, Code, BarChart3, FolderOpen, Brain, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  isConnected: boolean;
  isAIReady: boolean;
}

export function Sidebar({ activeView, onViewChange, isConnected, isAIReady }: SidebarProps) {
  const menuItems = [
    { id: 'chat', label: 'AI Chat', icon: MessageCircle, description: 'Conversation with Llama 3.3' },
    { id: 'code', label: 'Code Editor', icon: Code, description: 'AI-powered coding' },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, description: 'Analytics & insights' },
    { id: 'projects', label: 'Projects', icon: FolderOpen, description: 'Project management' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* AI Status */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center space-x-3 mb-4">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900"
            />
          </div>
          <div>
            <h3 className="font-semibold text-white">Llama 3.3 AI</h3>
            <p className="text-xs text-slate-400">
              ✅ Ready to assist with Llama 3.3
            </p>
          </div>
        </div>

        {/* AI Capabilities */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-xs text-slate-300">
            <Zap className="w-3 h-3 text-yellow-400" />
            <span>70B Parameters</span>
          </div>
          <div className="flex items-center space-x-2 text-xs text-slate-300">
            <Brain className="w-3 h-3 text-blue-400" />
            <span>Advanced Reasoning</span>
          </div>
          <div className="flex items-center space-x-2 text-xs text-slate-300">
            <Code className="w-3 h-3 text-green-400" />
            <span>Multi-Language Support</span>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4">
        <div className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <motion.button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-white'
                    : 'hover:bg-white/10 text-slate-300 hover:text-white'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-blue-400' : ''}`} />
                <div className="text-left">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs opacity-70">{item.description}</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </nav>

      {/* Status Footer */}
      <div className="p-4 border-t border-white/10">
        <div className="text-xs text-slate-500 space-y-1">
          <div>Status: ✅ Connected to Cloudflare</div>
          <div>AI Model: ✅ Llama 3.3 70B (Live)</div>
          <div>Edge Location: ✅ Global Edge Network</div>
        </div>
      </div>
    </div>
  );
}